import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Coach, CoachHoursReportRow, CoachHoursSessionRow } from '@court-sync/shared';
import { coachService } from '@/services/coach';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import { downloadCSV } from '@/utils/csvUtils';
import { cn } from '@/lib/utils';
import {
  DashboardDataCard,
  DashboardFilterCard,
  dashboardTableCellClass,
  dashboardTableDataRowClass,
  dashboardTableHeadClass,
  dashboardTableHeaderRowClass,
} from '@/components/dashboard/DashboardSectionCard';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { ChevronRight, Filter, Info } from 'lucide-react';
import { Switch } from '@/components/ui/Switch';
import { Label } from '@/components/ui/Label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover';

const FILTER_SELECT_CLASS =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

type BillingPeriodMode = 'month' | 'range';

function currentBillableMonth(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

/** `YYYY-MM` → first and last calendar day of that month (`YYYY-MM-DD`). */
function monthToPeriod(ym: string): { from: string; to: string } | null {
  if (!ym || !/^\d{4}-\d{2}$/.test(ym)) return null;
  const [year, month] = ym.split('-').map(Number);
  if (month < 1 || month > 12) return null;
  const mm = String(month).padStart(2, '0');
  const lastDay = new Date(year, month, 0).getDate();
  return {
    from: `${year}-${mm}-01`,
    to: `${year}-${mm}-${String(lastDay).padStart(2, '0')}`,
  };
}

function currentMonthDateRange(): { from: string; to: string } {
  const period = monthToPeriod(currentBillableMonth());
  return {
    from: period?.from ?? '',
    to: period?.to ?? '',
  };
}

function openMonthPicker(input: HTMLInputElement) {
  if (typeof input.showPicker !== 'function') return;
  try {
    input.showPicker();
  } catch {
    // showPicker may throw when not allowed in this browser context.
  }
}

function coachHoursBillByAttendanceStorageKey(academyId: string): string {
  return `courtsync.coachHours.billByAttendance.${academyId}`;
}

function readBillByAttendancePreference(academyId: string): boolean {
  try {
    return localStorage.getItem(coachHoursBillByAttendanceStorageKey(academyId)) === '1';
  } catch {
    return false;
  }
}

export interface CoachHoursReportProps {
  academyId: string;
  coaches: Coach[];
}

export function CoachHoursReport({ academyId, coaches }: CoachHoursReportProps) {
  const defaultMonthRange = useMemo(() => currentMonthDateRange(), []);
  const [periodMode, setPeriodMode] = useState<BillingPeriodMode>('month');
  const [billableMonth, setBillableMonth] = useState(currentBillableMonth);
  const [rangeStart, setRangeStart] = useState(defaultMonthRange.from);
  const [rangeEnd, setRangeEnd] = useState(defaultMonthRange.to);
  const period = useMemo(() => {
    if (periodMode === 'month') {
      return monthToPeriod(billableMonth);
    }
    if (!rangeStart || !rangeEnd || rangeStart > rangeEnd) {
      return null;
    }
    return { from: rangeStart, to: rangeEnd };
  }, [periodMode, billableMonth, rangeStart, rangeEnd]);
  const [coachFilter, setCoachFilter] = useState('');
  const [lessonType, setLessonType] = useState<'all' | 'group' | 'private' | 'camp'>('all');
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<CoachHoursReportRow[]>([]);
  const [selectedCoachId, setSelectedCoachId] = useState<string | null>(null);
  const [drilldown, setDrilldown] = useState<CoachHoursSessionRow[]>([]);
  const [loadingDrill, setLoadingDrill] = useState(false);
  const [includeRateAndPayout, setIncludeRateAndPayout] = useState(true);
  const [showAttendanceInfo, setShowAttendanceInfo] = useState(false);
  const [billByAttendance, setBillByAttendanceState] = useState(() =>
    readBillByAttendancePreference(academyId)
  );

  const setBillByAttendance = useCallback(
    (value: boolean) => {
      setBillByAttendanceState(value);
      try {
        localStorage.setItem(coachHoursBillByAttendanceStorageKey(academyId), value ? '1' : '0');
      } catch {
        /* localStorage unavailable */
      }
    },
    [academyId]
  );

  const load = useCallback(async () => {
    if (!period) {
      setRows([]);
      return;
    }
    setLoading(true);
    try {
      const data = await coachService.getCoachHoursWorked({
        academyId,
        from: period.from,
        to: period.to,
        ...(coachFilter ? { coachId: coachFilter } : {}),
        ...(lessonType !== 'all' ? { lessonType } : {}),
        billByAttendance,
      });
      setRows(data.aggregates);
    } catch (e: unknown) {
      setRows([]);
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [academyId, period, coachFilter, lessonType, billByAttendance]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadDrill = useCallback(
    async (coachId: string) => {
      if (!period) return;
      setLoadingDrill(true);
      setSelectedCoachId(coachId);
      try {
        const data = await coachService.getCoachHoursWorked({
          academyId,
          from: period.from,
          to: period.to,
          coachId,
          ...(lessonType !== 'all' ? { lessonType } : {}),
          detailed: true,
          billByAttendance,
        });
        setDrilldown(data.detailed ?? []);
      } catch {
        setDrilldown([]);
      } finally {
        setLoadingDrill(false);
      }
    },
    [academyId, period, lessonType, billByAttendance]
  );

  const coachName = (id: string) => {
    const c = coaches.find((x) => x.id === id);
    return c ? `${c.firstName} ${c.lastName}` : id;
  };

  const coachFileSlug = (id: string) =>
    coachName(id)
      .trim()
      .replace(/\s+/g, '-')
      .replace(/[^\w.-]/g, '');

  const totals = useMemo(() => {
    let minutes = 0;
    let sessions = 0;
    let payout = 0;
    for (const r of rows) {
      minutes += r.totalMinutes;
      sessions += r.totalSessions;
      payout += r.estimatedPayout ?? 0;
    }
    return { minutes, sessions, payout };
  }, [rows]);

  const exportMain = () => {
    if (!period) return;
    downloadCSV(
      `coach-hours_${period.from}_to_${period.to}.csv`,
      rows.map((r) => {
        const base: Record<string, string | number> = {
          Coach: `${r.firstName} ${r.lastName}`.trim() || coachName(r.coachId),
          Sessions: r.totalSessions,
          Hours: (r.totalMinutes / 60).toFixed(2),
        };
        if (includeRateAndPayout) {
          base.HourlyRate = r.hourlyRate ?? '';
          base.EstPayout = r.estimatedPayout ?? '';
        }
        return base;
      })
    );
  };

  const exportDrill = () => {
    if (!selectedCoachId || !period) return;
    downloadCSV(
      `coach-hours-drilldown_${coachFileSlug(selectedCoachId)}_${period.from}_to_${period.to}.csv`,
      drilldown.map((r) => ({
        Date: r.sessionDate,
        Lesson: r.lessonName,
        Type: r.lessonType,
        Start: r.startTime,
        End: r.endTime,
        Hours: (r.minutes / 60).toFixed(2),
      }))
    );
  };

  const closeDrillModal = () => {
    setSelectedCoachId(null);
    setDrilldown([]);
  };

  const handlePeriodModeChange = (mode: BillingPeriodMode) => {
    setPeriodMode(mode);
    if (mode === 'range') {
      const monthPeriod = monthToPeriod(billableMonth);
      if (monthPeriod) {
        setRangeStart(monthPeriod.from);
        setRangeEnd(monthPeriod.to);
      }
    }
  };

  const hasActiveFilters = coachFilter !== '' || lessonType !== 'all';

  const clearFilters = () => {
    setCoachFilter('');
    setLessonType('all');
  };

  return (
    <div className="space-y-6">
      <DashboardFilterCard
        title="Filters"
        description={
          billByAttendance
            ? 'Hours and estimated payout count only sessions where coach attendance is marked attended.'
            : 'Hours are based on scheduled sessions in the selected period. Tap a coach row to view session detail.'
        }
      >
          <div className="flex flex-col gap-3 min-[480px]:flex-row min-[480px]:items-end min-[480px]:justify-between">
            <div className="flex min-w-0 flex-wrap items-end gap-2 sm:gap-3">
              <div className="min-w-0 w-full min-[480px]:w-auto">
                <label
                  htmlFor="coach-hours-period-mode"
                  className="mb-1 block text-xs font-medium text-muted-foreground"
                >
                  Billing period
                </label>
                <select
                  id="coach-hours-period-mode"
                  aria-label="Billing period type"
                  value={periodMode}
                  onChange={(e) => handlePeriodModeChange(e.target.value as BillingPeriodMode)}
                  className={`${FILTER_SELECT_CLASS} h-9 min-w-[12rem] pr-10 min-[480px]:w-48`}
                >
                  <option value="month">Billable month</option>
                  <option value="range">Custom date range</option>
                </select>
              </div>
              {periodMode === 'month' ? (
                <div className="min-w-0 w-full min-[480px]:w-auto">
                  <label
                    htmlFor="coach-hours-month"
                    className="mb-1 block text-xs font-medium text-muted-foreground"
                  >
                    Month
                  </label>
                  <Input
                    id="coach-hours-month"
                    type="month"
                    aria-label="Billable month"
                    value={billableMonth}
                    onChange={(e) => setBillableMonth(e.target.value)}
                    onClick={(e) => openMonthPicker(e.currentTarget)}
                    className="relative h-9 w-full min-[480px]:w-44 cursor-pointer py-2 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0"
                  />
                </div>
              ) : (
                <>
                  <div className="min-w-0 w-full min-[480px]:w-auto">
                    <label
                      htmlFor="coach-hours-from"
                      className="mb-1 block text-xs font-medium text-muted-foreground"
                    >
                      From
                    </label>
                    <Input
                      id="coach-hours-from"
                      type="date"
                      aria-label="Coach hours period start date"
                      value={rangeStart}
                      max={rangeEnd || undefined}
                      onChange={(e) => setRangeStart(e.target.value)}
                      className="h-9 w-full min-[480px]:w-40"
                    />
                  </div>
                  <div className="min-w-0 w-full min-[480px]:w-auto">
                    <label
                      htmlFor="coach-hours-to"
                      className="mb-1 block text-xs font-medium text-muted-foreground"
                    >
                      To
                    </label>
                    <Input
                      id="coach-hours-to"
                      type="date"
                      aria-label="Coach hours period end date"
                      value={rangeEnd}
                      min={rangeStart || undefined}
                      onChange={(e) => setRangeEnd(e.target.value)}
                      className="h-9 w-full min-[480px]:w-40"
                    />
                  </div>
                </>
              )}
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="inline-flex shrink-0 items-center gap-2"
                  >
                    <Filter className="h-4 w-4" aria-hidden />
                    Filters
                    {hasActiveFilters ? (
                      <span className="ml-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary-600 px-1.5 text-xs text-white">
                        •
                      </span>
                    ) : null}
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-[min(450px,90vw)] max-h-[80vh] overflow-y-auto p-4"
                  align="start"
                >
                  <div className="space-y-4">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="font-semibold text-foreground">Filter report</h3>
                      {hasActiveFilters ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 text-muted-foreground"
                          onClick={clearFilters}
                        >
                          Clear
                        </Button>
                      ) : null}
                    </div>
                    <div className="space-y-3">
                      <div className="min-w-0">
                        <label
                          htmlFor="coach-hours-coach"
                          className="mb-1 block text-xs font-medium text-muted-foreground"
                        >
                          Coach
                        </label>
                        <select
                          id="coach-hours-coach"
                          aria-label="Coach hours coach filter"
                          className={FILTER_SELECT_CLASS}
                          value={coachFilter}
                          onChange={(e) => setCoachFilter(e.target.value)}
                        >
                          <option value="">All coaches</option>
                          {coaches.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.firstName} {c.lastName}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="min-w-0">
                        <label
                          htmlFor="coach-hours-lesson-type"
                          className="mb-1 block text-xs font-medium text-muted-foreground"
                        >
                          Lesson type
                        </label>
                        <select
                          id="coach-hours-lesson-type"
                          aria-label="Coach hours lesson type filter"
                          className={FILTER_SELECT_CLASS}
                          value={lessonType}
                          onChange={(e) => setLessonType(e.target.value as typeof lessonType)}
                        >
                          <option value="all">All</option>
                          <option value="group">Group</option>
                          <option value="private">Private</option>
                          <option value="camp">Camp</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
              <label
                htmlFor="coach-hours-include-payout"
                className="flex h-9 cursor-pointer select-none items-center gap-2.5 whitespace-nowrap text-sm text-foreground"
              >
                <input
                  id="coach-hours-include-payout"
                  type="checkbox"
                  className="size-4 shrink-0 rounded border-input text-primary focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  checked={includeRateAndPayout}
                  onChange={(e) => setIncludeRateAndPayout(e.target.checked)}
                />
                Rate & est. payout
              </label>
              <div className="flex h-9 items-center gap-2.5 whitespace-nowrap">
                <Switch
                  id="coach-hours-bill-by-attendance"
                  checked={billByAttendance}
                  onCheckedChange={setBillByAttendance}
                  aria-label="Pay by attendance"
                />
                <Label htmlFor="coach-hours-bill-by-attendance" className="cursor-pointer font-normal">
                  Pay by attendance
                </Label>
                <button
                  type="button"
                  onClick={() => setShowAttendanceInfo(true)}
                  className="inline-flex size-5 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                  aria-label="How coach attendance works"
                >
                  <Info className="size-4" aria-hidden />
                </button>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0"
                onClick={() => void load()}
                disabled={loading || !period}
              >
                Apply
              </Button>
            </div>
            <Button
              type="button"
              size="sm"
              className="w-full min-[480px]:w-auto"
              onClick={exportMain}
              disabled={loading || rows.length === 0 || !period}
            >
              Export CSV
            </Button>
          </div>
      </DashboardFilterCard>

      <DashboardDataCard
        title={
          <>
            Coach hours{billByAttendance ? ' (attended sessions)' : ''}
          </>
        }
        description={
          billByAttendance
            ? 'Hours and payout are based on sessions where coach attendance is marked attended.'
            : 'Tap or select a coach to view their sessions in a dialog.'
        }
      >
        {/* Mobile: stacked cards */}
        <div className="md:hidden">
          {loading ? (
            <p className="px-4 py-10 text-center text-sm text-muted-foreground">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="px-4 py-10 text-center text-sm text-muted-foreground">No data for this range.</p>
          ) : (
            <>
              <ul className="divide-y divide-border">
                {rows.map((r) => {
                  const name = `${r.firstName} ${r.lastName}`.trim() || coachName(r.coachId);
                  const hours = (r.totalMinutes / 60).toFixed(2);
                  return (
                    <li key={r.coachId}>
                      <button
                        type="button"
                        onClick={() => void loadDrill(r.coachId)}
                        className="flex w-full flex-col gap-3 px-4 py-4 text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background active:bg-muted/70"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-semibold leading-snug text-foreground">{name}</span>
                          <ChevronRight className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                        </div>
                        <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
                          <div className="space-y-0.5">
                            <dt className="text-xs font-medium text-muted-foreground">Sessions</dt>
                            <dd className="tabular-nums font-medium text-foreground">{r.totalSessions}</dd>
                          </div>
                          <div className="space-y-0.5">
                            <dt className="text-xs font-medium text-muted-foreground">Hours</dt>
                            <dd className="tabular-nums font-medium text-foreground">{hours}</dd>
                          </div>
                          {includeRateAndPayout ? (
                            <>
                              <div className="space-y-0.5">
                                <dt className="text-xs font-medium text-muted-foreground">Rate</dt>
                                <dd className="tabular-nums font-medium text-foreground">
                                  {r.hourlyRate ?? '—'}
                                </dd>
                              </div>
                              <div className="space-y-0.5">
                                <dt className="text-xs font-medium text-muted-foreground">Est. payout</dt>
                                <dd className="tabular-nums font-medium text-foreground">
                                  {r.estimatedPayout != null ? r.estimatedPayout.toFixed(2) : '—'}
                                </dd>
                              </div>
                            </>
                          ) : null}
                        </dl>
                      </button>
                    </li>
                  );
                })}
              </ul>
              <div className="border-t border-border bg-muted/30 px-4 py-4">
                <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Totals
                </div>
                <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
                  <div className="space-y-0.5">
                    <dt className="text-xs font-medium text-muted-foreground">Sessions</dt>
                    <dd className="tabular-nums font-semibold text-foreground">{totals.sessions}</dd>
                  </div>
                  <div className="space-y-0.5">
                    <dt className="text-xs font-medium text-muted-foreground">Hours</dt>
                    <dd className="tabular-nums font-semibold text-foreground">
                      {(totals.minutes / 60).toFixed(2)}
                    </dd>
                  </div>
                  {includeRateAndPayout ? (
                    <>
                      <div className="space-y-0.5">
                        <dt className="text-xs font-medium text-muted-foreground">Rate</dt>
                        <dd className="tabular-nums font-semibold text-muted-foreground">—</dd>
                      </div>
                      <div className="space-y-0.5">
                        <dt className="text-xs font-medium text-muted-foreground">Est. payout</dt>
                        <dd className="tabular-nums font-semibold text-foreground">
                          {totals.payout.toFixed(2)}
                        </dd>
                      </div>
                    </>
                  ) : null}
                </dl>
              </div>
            </>
          )}
        </div>

        {/* Desktop & tablet: table */}
        <div className="hidden md:block">
          <div className="overflow-x-auto">
            <Table aria-label="Coach hours by coach" className="min-w-[520px]">
              <TableHeader>
                <TableRow className={dashboardTableHeaderRowClass}>
                  <TableHead className={cn(dashboardTableHeadClass)}>
                    Coach
                  </TableHead>
                  <TableHead className={cn(dashboardTableHeadClass, 'text-right')}>
                    Sessions
                  </TableHead>
                  <TableHead className={cn(dashboardTableHeadClass, 'text-right')}>
                    Hours
                  </TableHead>
                  {includeRateAndPayout ? (
                    <>
                      <TableHead className={cn(dashboardTableHeadClass, 'text-right')}>
                        Rate
                      </TableHead>
                      <TableHead className={cn(dashboardTableHeadClass, 'text-right')}>
                        Est. payout
                      </TableHead>
                    </>
                  ) : null}
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={includeRateAndPayout ? 5 : 3}
                      className="px-4 py-12 text-center text-sm text-muted-foreground"
                    >
                      Loading…
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={includeRateAndPayout ? 5 : 3}
                      className="px-4 py-12 text-center text-sm text-muted-foreground"
                    >
                      No data for this range.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((r) => (
                    <TableRow
                      key={r.coachId}
                      className={cn('cursor-pointer', dashboardTableDataRowClass)}
                      onClick={() => void loadDrill(r.coachId)}
                    >
                      <TableCell className={cn(dashboardTableCellClass, 'font-medium text-foreground')}>
                        {`${r.firstName} ${r.lastName}`.trim() || coachName(r.coachId)}
                      </TableCell>
                      <TableCell className="px-4 py-3 text-right tabular-nums text-foreground">
                        {r.totalSessions}
                      </TableCell>
                      <TableCell className="px-4 py-3 text-right tabular-nums text-foreground">
                        {(r.totalMinutes / 60).toFixed(2)}
                      </TableCell>
                      {includeRateAndPayout ? (
                        <>
                          <TableCell className="px-4 py-3 text-right tabular-nums text-muted-foreground">
                            {r.hourlyRate ?? '—'}
                          </TableCell>
                          <TableCell className="px-4 py-3 text-right tabular-nums font-medium text-foreground">
                            {r.estimatedPayout != null ? r.estimatedPayout.toFixed(2) : '—'}
                          </TableCell>
                        </>
                      ) : null}
                    </TableRow>
                  ))
                )}
              </TableBody>
              {!loading && rows.length > 0 ? (
                <TableFooter>
                  <TableRow className="border-t-2 border-border bg-muted/40 hover:bg-muted/40">
                    <TableCell className="px-4 py-3 font-semibold text-foreground">Totals</TableCell>
                    <TableCell className="px-4 py-3 text-right tabular-nums font-semibold text-foreground">
                      {totals.sessions}
                    </TableCell>
                    <TableCell className="px-4 py-3 text-right tabular-nums font-semibold text-foreground">
                      {(totals.minutes / 60).toFixed(2)}
                    </TableCell>
                    {includeRateAndPayout ? (
                      <>
                        <TableCell className="px-4 py-3 text-right tabular-nums font-semibold text-muted-foreground">
                          —
                        </TableCell>
                        <TableCell className="px-4 py-3 text-right tabular-nums font-semibold text-foreground">
                          {totals.payout.toFixed(2)}
                        </TableCell>
                      </>
                    ) : null}
                  </TableRow>
                </TableFooter>
              ) : null}
            </Table>
          </div>
        </div>
      </DashboardDataCard>

      <Dialog open={showAttendanceInfo} onOpenChange={setShowAttendanceInfo}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>How coach attendance works</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-muted-foreground">
            <p>
              When <strong className="text-foreground">Allow coaches to mark their own attendance</strong> is
              enabled in{' '}
              <Link
                to="/admin/academy/settings?tab=advanced"
                className="font-medium text-primary hover:underline"
                onClick={() => setShowAttendanceInfo(false)}
              >
                Lesson Settings
              </Link>
              , coaches check in from the coach dashboard (My Attendance, My Schedule, and
              Today&apos;s Roster). Otherwise you mark coach attendance from Calendar session
              details.
            </p>
            <p>
              With <strong className="text-foreground">Pay by attendance</strong> enabled, only
              sessions where the coach is marked attended count toward hours and estimated payout.
              Unmarked past sessions are treated as not attended.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={selectedCoachId !== null}
        onOpenChange={(open) => {
          if (!open) closeDrillModal();
        }}
      >
        <DialogContent className="flex max-h-[min(90vh,720px)] w-[calc(100vw-1rem)] max-w-3xl flex-col gap-0 overflow-hidden p-0 sm:w-full">
          <DialogHeader className="shrink-0 space-y-1 border-b border-border px-4 pb-4 pt-2 text-left sm:px-6 sm:pb-4 sm:pt-3">
            <DialogTitle>Sessions</DialogTitle>
            <DialogDescription>
              {selectedCoachId ? coachName(selectedCoachId) : ''}
            </DialogDescription>
            <div className="pt-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full sm:w-auto"
                onClick={exportDrill}
                disabled={drilldown.length === 0 || loadingDrill}
              >
                Export drill-down CSV
              </Button>
            </div>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6">
            {loadingDrill ? (
              <p className="text-sm text-muted-foreground">Loading sessions…</p>
            ) : drilldown.length === 0 ? (
              <p className="text-sm text-muted-foreground">No session rows.</p>
            ) : (
              <>
                <ul className="flex flex-col gap-3 md:hidden">
                  {drilldown.map((r) => (
                    <li
                      key={`${r.sessionId}-${r.coachId}`}
                      className="rounded-lg border border-border bg-muted/20 px-4 py-3"
                    >
                      <div className="font-medium leading-snug text-foreground">{r.lessonName}</div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        <span className="capitalize">{r.lessonType}</span>
                        <span aria-hidden className="mx-1.5">
                          ·
                        </span>
                        <time dateTime={r.sessionDate}>{r.sessionDate}</time>
                      </div>
                      <dl className="mt-3 grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <dt className="text-xs font-medium text-muted-foreground">Time</dt>
                          <dd className="tabular-nums text-foreground">
                            {r.startTime}–{r.endTime}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs font-medium text-muted-foreground">Hours</dt>
                          <dd className="tabular-nums font-medium text-foreground">
                            {(r.minutes / 60).toFixed(2)}
                          </dd>
                        </div>
                      </dl>
                    </li>
                  ))}
                </ul>
                <div className="hidden overflow-x-auto rounded-lg border border-border md:block">
                  <Table
                    aria-label={
                      selectedCoachId ? `Sessions for ${coachName(selectedCoachId)}` : 'Coach sessions'
                    }
                    className="min-w-[560px]"
                  >
                    <TableHeader>
                      <TableRow className="border-border hover:bg-transparent">
                        <TableHead className={cn(dashboardTableHeadClass)}>
                          Date
                        </TableHead>
                        <TableHead className={cn(dashboardTableHeadClass)}>
                          Lesson
                        </TableHead>
                        <TableHead className={cn(dashboardTableHeadClass)}>
                          Type
                        </TableHead>
                        <TableHead className={cn(dashboardTableHeadClass)}>
                          Time
                        </TableHead>
                        <TableHead className="whitespace-nowrap px-4 text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          Hours
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {drilldown.map((r) => (
                        <TableRow key={`${r.sessionId}-${r.coachId}`} className="border-border">
                          <TableCell className="whitespace-nowrap px-4 py-2.5 tabular-nums text-foreground">
                            {r.sessionDate}
                          </TableCell>
                          <TableCell className="max-w-[14rem] truncate px-4 py-2.5 text-foreground" title={r.lessonName}>
                            {r.lessonName}
                          </TableCell>
                          <TableCell className="whitespace-nowrap px-4 py-2.5 capitalize text-muted-foreground">
                            {r.lessonType}
                          </TableCell>
                          <TableCell className="whitespace-nowrap px-4 py-2.5 tabular-nums text-foreground">
                            {r.startTime}–{r.endTime}
                          </TableCell>
                          <TableCell className="px-4 py-2.5 text-right tabular-nums font-medium text-foreground">
                            {(r.minutes / 60).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
